// Yolutap — Pre-test session server functions (Task 2)
// All DB writes happen server-side via the admin client (RLS bypass) so we can
// safely mutate `status` / `paid_at` without exposing those fields to clients.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { resolveOptionalUserId } from "@/integrations/supabase/optional-auth";
import { getAgeGroupConfig } from "@/lib/yolutap/ageGroupConfig";
import { notifyAdmin } from "@/lib/notifications.server";
import { verifyTurnstile, assertTurnstileOk } from "@/lib/security/turnstile.server";
import { reportPaymentFailure } from "@/lib/payment/payment-alerts";
import { isMissingRefColumn, partnerIdByRef } from "@/lib/tutor-ref.server";
import { callerIp, hashIp } from "@/lib/request-ip.server";
import { utmColumns } from "@/lib/utm";
import type { LanguageKey } from "@/lib/yolutap/types";

// ---------- shared DTO ----------

// Live tiers are basic + premium. Older rows may carry the legacy
// "plus" value (the pre-launch enum default until this PR); the type
// keeps it so reads from such rows stay narrowed, but payment paths
// (PayScreen + azericard-start) fail-closed on it via isKnownTier()
// rather than charge an ambiguous amount. See src/lib/payment/pricing.ts.
export type PackageType = "basic" | "premium" | "plus";

export type PretestSessionDTO = {
  id: string;
  access_token: string;
  child_name: string;
  age_group: string;
  language_key: LanguageKey;
  parent_email: string;
  parent_consent_at: string;
  test_version_id: string;
  parent_form_version_id: string;
  report_template_version_id: string;
  status: "pending_payment" | "paid" | "in_progress" | "complete" | "expired";
  package_type: PackageType;
  amount_cents: number | null;
  currency: string | null;
  paid_at: string | null;
  expires_at: string;
  created_at: string;
  // Школьное место. Проставляется только при погашении кода из школьной
  // пачки, поэтому непустое значение и есть признак «ребёнок сидит на уроке,
  // взрослого рядом нет» — по нему выбирается установочный экран.
  school_id: string | null;
};

function toDTO(row: Record<string, unknown>): PretestSessionDTO {
  return {
    id: String(row.id),
    access_token: String(row.access_token),
    child_name: String(row.child_name),
    age_group: String(row.age_group),
    language_key: row.language_key as LanguageKey,
    // Пусто, а не «null»: с 20261001 адреса может не быть вовсе (школьное
    // место без списка), а String(null) даёт строку из четырёх букв, которая
    // выглядит как адрес.
    parent_email: row.parent_email == null ? "" : String(row.parent_email),
    parent_consent_at: String(row.parent_consent_at),
    test_version_id: String(row.test_version_id),
    parent_form_version_id: String(row.parent_form_version_id),
    report_template_version_id: String(row.report_template_version_id),
    status: row.status as PretestSessionDTO["status"],
    package_type: ((row.package_type as PackageType) ?? "basic") as PackageType,
    amount_cents: (row.amount_cents as number | null) ?? null,
    currency: (row.currency as string | null) ?? null,
    paid_at: (row.paid_at as string | null) ?? null,
    expires_at: String(row.expires_at),
    created_at: String(row.created_at),
    school_id: (row.school_id as string | null) ?? null,
  };
}

// ---------- create session ----------

const CreateInput = z.object({
  child_name: z.string().trim().min(1).max(80),
  age_group: z.enum(["10_11", "12_13", "14_15", "16_18", "18_22"]),
  language_key: z.enum(["ru", "az", "en"]),
  parent_email: z.string().trim().email().max(255),
  // Optional student email — enables the child_email claim path (report
  // surfaces in the STUDENT's cabinet). Blank → undefined → NULL.
  child_email: z.string().trim().email().max(255).optional(),
  parent_consent: z.literal(true),
  // Live tiers only — UI on /start sends exactly one of these. The
  // legacy "plus" value is no longer accepted on session creation
  // (existing rows with that value still read fine via PackageType
  // but fail-closed at PayScreen / azericard-start signing time).
  package_type: z.enum(["basic", "premium"]).optional().default("basic"),
  // Cloudflare Turnstile token from <TurnstileWidget /> on /start.
  // Server verifies via siteverify before any DB work — see
  // src/lib/security/turnstile.server.ts.
  // Капча не прошла и после повтора — токена нет: сессия всё равно создаётся
  // с пометкой captcha_failed (см. обработчик, лимит CAPTCHA_FAILED_PER_HOUR).
  turnstile_token: z.string().max(2048).optional(),
  // Код анкеты карты из ссылки репетитора (?ref=, src/lib/tutor-ref.ts). Только
  // учёт «кто привёл»: на тест, цену и отчёт не влияет.
  ref: z.string().max(16).optional(),
  // utm с первого захода на сайт (src/lib/utm.ts). Странное значение — не отказ: поле просто пустое.
  utm_source: z.unknown().optional(),
  utm_campaign: z.unknown().optional(),
});

/** Стартов теста без пройденной капчи в час с одного адреса (решение владельца 24.10.2026). */
export const CAPTCHA_FAILED_PER_HOUR = 3;

/**
 * Имя нарушенного CHECK из ответа PostgREST («violates check constraint "x"»).
 * Только имя: строка данных (details) с почтой и именем в Sentry не едет.
 */
export function checkConstraintName(error: { message?: string | null } | null | undefined): string | null {
  const m = /violates check constraint "([A-Za-z0-9_]{1,63})"/.exec(error?.message ?? "");
  return m ? m[1] : null;
}

export const createPretestSession = createServerFn({ method: "POST" })
  .inputValidator((input) => CreateInput.parse(input))
  .handler(async ({ data }) => {
    // Gate 1: Turnstile — verify BEFORE touching DB so spam/bot floods
    // don't consume IDs / cron budget. Fails closed in production if
    // TURNSTILE_SECRET_KEY is unset (deployment-order safeguard).
    const verdict = await verifyTurnstile(data.turnstile_token);
    // ЕДИНСТВЕННЫЙ СЛЕД ЭТОГО ШАГА. Проверка стоит ДО вставки строки (чтобы
    // поток ботов не съедал идентификаторы), поэтому непройденная капча не
    // оставляет в базе ничего: ни сессии, ни заказа. Инцидент 5 августа с
    // тестовым ключом уронил оплату, и в данных от него нет ни одной записи —
    // мы не знаем даже, сколько человек он стоил. Сообщение в мониторинг —
    // самый дешёвый способ сделать шаг видимым, не заводя схему.
    if (!verdict.ok) {
      reportPaymentFailure("captcha_rejected", {
        reason: verdict.reason,
        codes: (verdict.codes ?? []).join(","),
      });
    }
    // ИЗ-ЗА КАПЧИ НИКТО НЕ ТЕРЯЕТСЯ (24.10.2026). 164 события за 30 дней, в том
    // числе на /start: встроенные браузеры (Facebook, Instagram) и сгоревший
    // токен. Капча не прошла — сессия создаётся с captcha_failed = true, не
    // больше CAPTCHA_FAILED_PER_HOUR в час с одного адреса. Секрета нет в проде —
    // по-прежнему отказ: это ошибка выкатки (инцидент 5 августа).
    let captchaFailed: { ip_hash: string | null } | null = null;
    if (!verdict.ok) {
      if (verdict.reason === "missing_secret_prod") assertTurnstileOk(verdict);
      const ipHash = await hashIp(callerIp());
      if (ipHash) {
        const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
        const { count, error: cErr } = await supabaseAdmin
          .from("pretest_sessions")
          .select("id", { count: "exact", head: true })
          .eq("captcha_failed" as never, true as never)
          .eq("ip_hash" as never, ipHash as never)
          .gte("created_at", since);
        if (cErr) console.error("[sessions] captcha limit read", cErr.message);
        if ((count ?? 0) >= CAPTCHA_FAILED_PER_HOUR) {
          reportPaymentFailure("captcha_rate_limited", { reason: verdict.reason });
          throw new Error("rate_limited");
        }
      }
      captchaFailed = { ip_hash: ipHash };
    }

    const cfg = getAgeGroupConfig(data.age_group);
    if (cfg.status !== "active") throw new Error("Age group not available yet");

    // Bind the session to the caller when the flow was started from the
    // cabinet (signed in). Anonymous visitors resolve to null and the insert
    // is byte-for-byte what it was — the public funnel is unchanged. This is
    // what stops sessions from being born ownerless and needing the claim
    // banner to reunite them later.
    const userId = await resolveOptionalUserId();

    const referredBy = await partnerIdByRef(data.ref);
    const insertSession = (extra: Record<string, unknown>) => supabaseAdmin
      .from("pretest_sessions")
      // Cast: child_email is a new column not yet in generated types.ts.
      .insert({
        ...extra,
        ...(userId ? { user_id: userId } : {}),
        child_name: data.child_name,
        age_group: data.age_group,
        language_key: data.language_key,
        parent_email: data.parent_email.toLowerCase(),
        child_email: data.child_email ? data.child_email.toLowerCase() : null,
        parent_consent_at: new Date().toISOString(),
        test_version_id: cfg.test_version_id,
        parent_form_version_id: cfg.parent_form_version_id,
        report_template_version_id: cfg.report_template_version_id,
        package_type: data.package_type,
      } as never)
      .select("*")
      .single();
    const marks = {
      ...(captchaFailed ? { captcha_failed: true, ip_hash: captchaFailed.ip_hash } : {}),
      ...(referredBy ? { referred_by_partner_id: referredBy } : {}),
    };
    const utm = utmColumns(data);
    let { data: row, error } = await insertSession({ ...marks, ...utm });
    // До наката 20261111120000 колонок utm нет: сессию не теряем, пишем без них.
    if (error && Object.keys(utm).length && /utm_source|utm_campaign/.test(error.message ?? "")) {
      console.error("[sessions] utm не записан: миграция 20261111120000 не накатана");
      ({ data: row, error } = await insertSession(marks));
    }
    // До наката 20261109120000 (капча) или 20261107120000 («кто привёл») колонок нет:
    // сессию не теряем — оплата важнее пометок.
    if (error && Object.keys(marks).length && (isMissingRefColumn(error) || /captcha_failed|ip_hash/.test(error.message ?? ""))) {
      console.error("[sessions] пометки не записаны: миграция 20261109120000 или 20261107120000 не накатана");
      ({ data: row, error } = await insertSession({}));
    }
    // 23514 — CHECK базы (прод 03.11.2026: 13 попыток, 0 сессий; ни одной сессии
    // с captcha_failed за всё время). Пометки, utm и «кто привёл» не обязательны:
    // без них сессия важнее. Один повтор без них, имя ограничения — в Sentry.
    if (error?.code === "23514" && Object.keys({ ...marks, ...utm }).length) {
      reportPaymentFailure("start_session_insert_retried", { code: "23514", constraint: checkConstraintName(error), captcha_failed: !!captchaFailed });
      ({ data: row, error } = await insertSession({}));
    }

    if (error || !row) {
      // Поломка формы /start — в Sentry с тегом form_health=submit_error (src/lib/form-health.ts).
      // Имя нарушенного ограничения — без строки данных: в ней почта и имя.
      reportPaymentFailure("start_session_insert_failed", { code: String(error?.code ?? ""), constraint: checkConstraintName(error), captcha_failed: !!captchaFailed });
      throw new Error(error?.message ?? "Could not create session");
    }
    await notifyAdmin({
      kind: "session_created",
      title: `Новая сессия: ${row.child_name}`,
      body: `${row.parent_email} · ${row.language_key.toUpperCase()} · ${row.age_group} · ${data.package_type}`,
      session_id: String(row.id),
    });
    return toDTO(row);
  });

// ---------- read session by token ----------

const TokenInput = z.object({ access_token: z.string().min(8).max(128) });

export const getSessionByToken = createServerFn({ method: "POST" })
  .inputValidator((input) => TokenInput.parse(input))
  .handler(async ({ data }) => {
    const { data: row, error } = await supabaseAdmin
      .from("pretest_sessions")
      .select("*")
      .eq("access_token", data.access_token)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!row) return null;
    // Treat expired sessions as not-accessible (same as not-found from
    // the caller's perspective). pretest_sessions.expires_at defaults to
    // now()+30d, the test-runner already refuses to mutate an expired
    // row (test-runner.functions.ts:86), and result.functions.ts:165
    // explicitly throws — this is the missing third surface that was
    // still returning a usable DTO after the TTL.
    if (row.status === "expired") return null;
    return toDTO(row);
  });

/**
 * Только язык сессии — для `<html lang>` уже в серверном HTML (загрузчик
 * /session/$token). Одна колонка, без DTO. Не бросает: ошибка или чужой токен
 * дают null, и страница печатается как раньше, языком маршрута.
 */
export const getSessionLanguage = createServerFn({ method: "POST" })
  .inputValidator((input) => TokenInput.parse(input))
  .handler(async ({ data }): Promise<LanguageKey | null> => {
    const { data: row, error } = await supabaseAdmin
      .from("pretest_sessions")
      .select("language_key")
      .eq("access_token", data.access_token)
      .maybeSingle();
    if (error || !row) return null;
    const l = row.language_key;
    return l === "ru" || l === "az" || l === "en" ? l : null;
  });

// ---------- abandon reason (from email reason-link) ----------

const CaptureAbandonReasonInput = z.object({
  access_token: z.string().min(8).max(128),
  reason: z.enum(["price", "unsure", "payment", "later"]),
});

/**
 * Capture which reason-link a parent clicked from the abandoned-checkout
 * email. First-click wins — the UPDATE includes `WHERE abandon_reason IS
 * NULL` so a second click does NOT overwrite the first answer. This is
 * deliberate: the first hesitation is usually more honest than a click
 * on a second visit (after the parent has seen the page again).
 *
 * Lookup is by access_token (no auth — the token IS the auth). Returns
 * { ok: true } regardless of whether the update touched a row, so the
 * caller can fire-and-forget without leaking whether the click was
 * counted.
 *
 * Never throws on caller side: the UI's primary job is to land the
 * parent on PayScreen for a second pay attempt; reason logging is a
 * side benefit, not a blocker.
 */
export const captureAbandonReason = createServerFn({ method: "POST" })
  .inputValidator((input) => CaptureAbandonReasonInput.parse(input))
  .handler(async ({ data }) => {
    try {
      await supabaseAdmin
        .from("pretest_sessions")
        .update({
          abandon_reason: data.reason,
          abandon_reason_at: new Date().toISOString(),
        } as never)
        .eq("access_token", data.access_token)
        .is("abandon_reason", null);
    } catch (e) {
      console.error("[captureAbandonReason] failed", e);
    }
    return { ok: true as const };
  });

// mockPaySession was removed in feat/wire-frontend-paywall-azericard. It
// previously flipped pretest_sessions.status="paid" without any payment —
// a pre-AC stub from before /api/payment/azericard-start landed. Two
// frontend paths (start.tsx and session.$token.tsx PayScreen) called it,
// so every real user was reaching a paid session without being charged.
//
// Activation now happens exclusively via the AzeriCard callback handler
// (src/routes/api/payment/azericard-callback.ts) on RC=00 with a verified
// P_SIGN. Local dev / manual reconciliation paths (admin tooling) write
// the row directly through supabaseAdmin.
//
// PRETEST_PRICE was also removed. The displayed amount + the signed
// amount now both come from src/lib/payment/pricing.ts (PRICING_AZN)
// keyed by pretest_sessions.package_type. See that file for the
// single-source-of-truth contract.
